import { Tooltip } from '@material-ui/core';
import { cloneDeep } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import {
  EAllowStatus,
  ELogicalOperator,
  TargetingPresetKeys,
} from '~/src/dash/apiV2/targeting/targetingPresets';
import { useFormBuilderStore } from '~/src/dash/components/forms/formBuilder/contexts';
import { IncludeExcludeToggle } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/common/IncludeExcludeToggle';
import { LogicToggle } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/common/LogicToggle';
import { ILocalTargetingUI } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/LocalTargetingBuilder/common';
import { LocationForm } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/LocalTargetingBuilder/LocationForm';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { Col, Cols, Row, Rows, spacing, TextButton } from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';

const rowHeight = 32;

// DO NOT REUSE OR COPY/PASTE. THIS IS A CUSTOM SOLUTION COMPONENT
export const LocalTargetingBuilder = () => {
  const { object, defaultObject } = useFormBuilderStore();

  const logicKey = TargetingPresetKeys.LocalTargetingLogic;
  const allowKey = TargetingPresetKeys.LocalTargetingIsAllowed;

  const [operator, setOperator] = useState(object.LocalTargetingLogic);
  const [status, setStatus] = useState(object.LocalTargetingIsAllowed);
  const [locations, setLocations] = useState<ILocalTargetingUI[]>(object.LocalTargetingUI);

  const [counter, setCounter] = useState(1);

  useEffect(() => {
    return () => {
      object.LocalTargeting = defaultObject.LocalTargeting;
      object.LocalTargetingUI = defaultObject.LocalTargetingUI;
      object[logicKey] = defaultObject[logicKey];
      object[allowKey] = defaultObject[allowKey];
    };
  }, []);

  const handleToggleOperator = () => {
    const newOperator =
      operator === ELogicalOperator.AND ? ELogicalOperator.OR : ELogicalOperator.AND;
    object[logicKey] = newOperator;
    setOperator(newOperator);
  };

  const handleToggleStatus = () => {
    const newStatus = status === EAllowStatus.ALLOW ? EAllowStatus.DENY : EAllowStatus.ALLOW;
    object[allowKey] = newStatus;
    setStatus(newStatus);
  };

  const handleAddLocation = () => {
    unstable_batchedUpdates(() => {
      setLocations((prevLocations) => {
        const clonedLocations = cloneDeep(prevLocations);
        clonedLocations.push({
          type: 'country',
          country: '',
          states: '',
          areStatesCommaSeparated: false,
          cities: '',
          areCitiesCommaSeparated: false,
          zips: '',
          dmas: '',
          areDMAsCommaSeparated: false,
          reactKey: counter,
        });
        object.LocalTargetingUI = clonedLocations;
        return clonedLocations;
      });
      setCounter(counter + 1);
    });
  };

  const handleRemoveLocation = (idx: number) => {
    setLocations((prevLocations) => {
      const clonedLocations = cloneDeep(prevLocations);
      clonedLocations.splice(idx, 1);
      object.LocalTargetingUI = clonedLocations;
      return clonedLocations;
    });
  };

  return (
    <Rows gap={spacing.small} noPadding>
      <Row noPadding height={rowHeight}>
        <Cols gap={spacing.huge} style={{ alignItems: 'center', minHeight: `${rowHeight}px` }}>
          <Col>
            <Tooltip
              title='Combine any layer of geo targeting to granularly define the location where this bidder should be targeted to. Targeting will be enforced looking at all params set per location. Add multiple locations and combine them using the AND / OR logic to achieve desired targeting.'
              placement='top'
              interactive
            >
              <div style={{ display: 'flex' }}>
                <IconV2 name='info-static' color={publicaColors.gray} />
              </div>
            </Tooltip>
          </Col>
          <Col>
            <LogicToggle
              operator={operator}
              handleToggleOperator={handleToggleOperator}
              key={logicKey}
              label='Logic'
            />
          </Col>
          <Col>
            <IncludeExcludeToggle
              status={status}
              handleToggleStatus={handleToggleStatus}
              key={allowKey}
            />
          </Col>
        </Cols>
      </Row>
      <Row noPadding>
        <Rows gap={spacing.base}>
          {locations.map((location, i) => {
            const isRemoveDisabled = locations.length === 1 && i === 0;
            return (
              <Row noPadding key={location.reactKey}>
                <LocationForm
                  location={location}
                  index={i}
                  isRemoveDisabled={isRemoveDisabled}
                  key={location.reactKey}
                  handleRemoveLocation={handleRemoveLocation}
                />
              </Row>
            );
          })}
        </Rows>
      </Row>
      <Row noHorizontalPadding smallVerticalPadding>
        <TextButton
          size='small'
          onClick={handleAddLocation}
          disabled={object.LocalTargetingUI.length >= 20}
        >
          + Add Location
        </TextButton>
      </Row>
    </Rows>
  );
};
